import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFileUpload, FaArrowLeft } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../config/api';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';

interface AvailableFile {
  url: string;
  filename: string;
  date: string | null;
  version: string | null;
  size_estimate: string;
}

const MBSItemUpload: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  
  const [uploading, setUploading] = useState(false);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [parsedCount, setParsedCount] = useState(0);
  const [availableFiles, setAvailableFiles] = useState<AvailableFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [importingFromUrl, setImportingFromUrl] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [progressMessage, setProgressMessage] = useState<string>('');

  const pollTaskStatus = async (taskId: string) => {
    if (!token) return;
    
    const maxAttempts = 300; // 5 minutes max (1 second intervals)
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await apiCall(
          `/billing/mbs-items/task_status/?task_id=${taskId}`,
          { method: 'GET' },
          token
        );
        
        if (!response.ok) {
          throw new Error('Failed to get task status');
        }
        
        const data = await response.json();
        
        if (data.status === 'PROGRESS') {
          // Update progress
          if (data.progress) {
            setProgress(data.progress.percent || 0);
            setProgressMessage(data.progress.status || 'Processing...');
          }
          
          // Continue polling
          if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, 1000);
          } else {
            showToast('Upload is taking longer than expected. Please check the MBS Items page later.', 'warning');
            setUploading(false);
            setImportingFromUrl(false);
            setTaskId(null);
          }
        } else if (data.status === 'SUCCESS') {
          // Task completed
          const result = data.result;
          showToast(
            `Successfully processed ${result.count || result.total_processed || 0} MBS items (${result.items_created} created, ${result.items_updated} updated)`,
            'success'
          );
          setProgress(100);
          setUploading(false);
          setImportingFromUrl(false);
          setTaskId(null);
          
          // Reset after 3 seconds
          setTimeout(() => {
            setFileContent(null);
            setFileName('');
            setParsedCount(0);
            setProgress(0);
            setProgressMessage('');
          }, 3000);
        } else if (data.status === 'FAILURE') {
          // Task failed
          showToast(`Upload failed: ${data.error || 'Unknown error'}`, 'error');
          setUploading(false);
          setImportingFromUrl(false);
          setTaskId(null);
          setProgress(0);
          setProgressMessage('');
        } else if (data.status === 'PENDING') {
          setProgressMessage('Starting upload...');
          // Continue polling
          if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, 1000);
          }
        }
      } catch (error) {
        console.error('Error polling task status:', error);
        showToast('Error checking upload status', 'error');
        setUploading(false);
        setImportingFromUrl(false);
        setTaskId(null);
        setProgress(0);
        setProgressMessage('');
      }
    };
    
    poll();
  };

  const parseXMLContent = (xmlString: string) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
    const dataElements = xmlDoc.getElementsByTagName('Data');

    return Array.from(dataElements).map(data => {
      const getValue = (tagName: string) => {
        const element = data.getElementsByTagName(tagName)[0];
        return element ? element.textContent : null;
      };

      return {
        itemNum: getValue('ItemNum'),
        subItemNum: getValue('SubItemNum'),
        itemStartDate: getValue('ItemStartDate'),
        itemEndDate: getValue('ItemEndDate'),
        category: getValue('Category'),
        groupCode: getValue('Group'),
        subGroup: getValue('SubGroup'),
        subHeading: getValue('SubHeading'),
        itemType: getValue('ItemType'),
        feeType: getValue('FeeType'),
        providerType: getValue('ProviderType'),
        newItem: getValue('NewItem'),
        itemChange: getValue('ItemChange'),
        anaesChange: getValue('AnaesChange'),
        descriptorChange: getValue('DescriptorChange'),
        feeChange: getValue('FeeChange'),
        emsnChange: getValue('EMSNChange'),
        emsnCap: getValue('EMSNCap'),
        benefitType: getValue('BenefitType'),
        benefitStartDate: getValue('BenefitStartDate'),
        feeStartDate: getValue('FeeStartDate'),
        scheduleFee: getValue('ScheduleFee'),
        benefit100: getValue('Benefit100'),
        basicUnits: getValue('BasicUnits'),
        emsnStartDate: getValue('EMSNStartDate'),
        emsnEndDate: getValue('EMSNEndDate'),
        emsnFixedCapAmount: getValue('EMSNFixedCapAmount'),
        emsnMaximumCap: getValue('EMSNMaximumCap'),
        emsnPercentageCap: getValue('EMSNPercentageCap'),
        emsnDescription: getValue('EMSNDescription'),
        emsnChangeDate: getValue('EMSNChangeDate'),
        descriptionStartDate: getValue('DescriptionStartDate'),
        description: getValue('Description'),
        qfeStartDate: getValue('QFEStartDate'),
        qfeEndDate: getValue('QFEEndDate'),
        derivedFeeStartDate: getValue('DerivedFeeStartDate'),
        derivedFee: getValue('DerivedFee')
      };
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);

    try {
      const text = await file.text();
      const parsedData = parseXMLContent(text);
      setFileContent(text);
      setParsedCount(parsedData.length);
      showToast(`File loaded successfully. Found ${parsedData.length} MBS items. Ready to upload.`, 'success');
    } catch (error) {
      showToast(`Error reading file: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    }
  };

  const fetchAvailableFiles = async () => {
    if (!token) return;
    
    setLoadingFiles(true);
    try {
      const response = await apiCall(
        '/billing/mbs-items/available_files/',
        { method: 'GET' },
        token
      );

      if (response.ok) {
        const data = await response.json();
        setAvailableFiles(data.files || []);
        if (data.files && data.files.length > 0) {
          showToast(`Found ${data.files.length} available MBS files`, 'success');
        } else {
          showToast('No available files found', 'warning');
        }
      }
    } catch (error) {
      console.error('Error fetching available files:', error);
      showToast('Error fetching available files', 'error');
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleImportFromUrl = async (url: string, filename: string) => {
    if (!token) return;
    
    setImportingFromUrl(true);
    
    try {
      const response = await apiCall(
        '/billing/mbs-items/import_from_url/',
        {
          method: 'POST',
          body: JSON.stringify({ url }),
        },
        token
      );

      if (!response.ok) {
        throw new Error('Import failed');
      }

      const result = await response.json();
      
      if (result.task_id) {
        // Background task started
        setTaskId(result.task_id);
        setProgress(0);
        setProgressMessage('Starting import...');
        showToast(`Import started for ${filename}`, 'info');
        pollTaskStatus(result.task_id);
      } else {
        // Immediate response (shouldn't happen with new implementation)
        showToast(
          `Successfully imported ${result.total_processed} MBS items (${result.items_created} created, ${result.items_updated} updated) from ${filename}`,
          'success'
        );
        setImportingFromUrl(false);
      }
    } catch (error) {
      showToast(
        `Error importing from URL: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
      setImportingFromUrl(false);
    }
  };

  const handleUpload = async () => {
    if (!fileContent) {
      showToast('Please select a file first', 'error');
      return;
    }

    setUploading(true);
    try {
      const parsedData = parseXMLContent(fileContent);

      if (!token) {
        // Demo mode
        setTimeout(() => {
          showToast(`Successfully uploaded ${parsedData.length} MBS items to the database`, 'success');
          setUploading(false);
        }, 2000);
        return;
      }

      const response = await apiCall(
        '/billing/mbs-items/upload/',
        {
          method: 'POST',
          body: JSON.stringify({ items: parsedData }),
        },
        token
      );

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      
      if (result.task_id) {
        // Background task started
        setTaskId(result.task_id);
        setProgress(0);
        setProgressMessage('Starting upload...');
        showToast('Upload started in background', 'info');
        pollTaskStatus(result.task_id);
      } else {
        // Immediate response (demo mode or old API)
        showToast(
          `Successfully uploaded ${result.count || parsedData.length} MBS items`,
          'success'
        );
        setUploading(false);
        
        // Reset after 3 seconds
        setTimeout(() => {
          setFileContent(null);
          setFileName('');
          setParsedCount(0);
        }, 3000);
      }
    } catch (error) {
      showToast(
        `Error uploading data: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
      setUploading(false);
    }
  };

  return (
    <>
      <Toast message={toast.message} type={toast.type} visible={toast.visible} onClose={hideToast} />
      <div className="flex bg-base-200" style={{ height: 'calc(100vh - 140px)' }}>
        <div className="w-full p-6 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/billing/mbs-items')}
            className="btn btn-ghost btn-sm"
          >
            <FaArrowLeft className="mr-2" />
            Back to MBS Items
          </button>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - 2/3 width */}
          <div className="lg:col-span-2">
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <h1 className="card-title text-2xl mb-2">
                  <FaFileUpload className="text-primary" />
                  MBS Schedule XML Uploader
                </h1>

                <div className="divider">Available MBS Files</div>

                {/* Available Files Section */}
                <div className="mb-6">
                  <p className="text-sm opacity-60 mb-4">
                    Download and import MBS schedules directly from the official government website.
                  </p>
                  <button
                    onClick={fetchAvailableFiles}
                    disabled={loadingFiles || importingFromUrl}
                    className="btn btn-secondary btn-sm mb-4"
                  >
                    {loadingFiles ? (
                      <>
                        <span className="loading loading-spinner loading-xs"></span>
                        Loading Files...
                      </>
                    ) : (
                      'Fetch Available Files'
                    )}
                  </button>

                  {availableFiles.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Filename</th>
                            <th>Date</th>
                            <th>Version</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {availableFiles.map((file, index) => (
                            <tr key={index}>
                              <td className="font-mono text-xs">{file.filename}</td>
                              <td>{file.date ? `${file.date.substring(0, 4)}-${file.date.substring(4, 6)}-${file.date.substring(6, 8)}` : 'N/A'}</td>
                              <td>{file.version || 'N/A'}</td>
                              <td>
                                <button
                                  onClick={() => handleImportFromUrl(file.url, file.filename)}
                                  disabled={importingFromUrl || uploading}
                                  className="btn btn-primary btn-xs"
                                >
                                  {importingFromUrl ? (
                                    <span className="loading loading-spinner loading-xs"></span>
                                  ) : (
                                    'Import'
                                  )}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="divider">Or Upload File</div>

            {/* File Upload Area */}
            <div className="form-control mb-6">
              <label className="label">
                <span className="label-text font-semibold">Select MBS XML File</span>
              </label>
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-lg cursor-pointer hover:bg-base-200 border-base-300">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <FaFileUpload className="w-12 h-12 mb-4 text-base-content opacity-40" />
                    <p className="mb-2 text-sm">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs opacity-60">XML files only</p>
                    {fileName && (
                      <div className="mt-4 px-4 py-2 bg-primary text-primary-content rounded-lg">
                        <p className="text-sm font-semibold">{fileName}</p>
                        <p className="text-xs">{parsedCount} items found</p>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept=".xml"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                </label>
              </div>
            </div>

            {/* Progress Bar */}
            {(uploading || importingFromUrl) && progress > 0 && (
              <div className="mb-6">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-semibold">{progressMessage}</span>
                  <span className="text-sm font-semibold">{progress}%</span>
                </div>
                <progress className="progress progress-primary w-full" value={progress} max="100"></progress>
              </div>
            )}

            {/* Upload Button */}
            <div className="card-actions justify-end">
              <button
                onClick={() => navigate('/billing/mbs-items')}
                className="btn btn-ghost"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!fileContent || uploading}
                className="btn btn-primary"
              >
                {uploading ? (
                  <>
                    <span className="loading loading-spinner"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <FaFileUpload className="mr-2" />
                    Upload to Database
                  </>
                )}
              </button>
            </div>
              </div>
            </div>
          </div>

          {/* Info Sidebar - 1/3 width */}
          <div className="space-y-4">
            <div className="alert alert-info">
              <div>
                <h4 className="font-bold mb-2">About MBS Schedules</h4>
                <ul className="list-disc list-inside space-y-1 opacity-80 text-sm">
                  <li>MBS schedules contain Medicare Benefits Schedule item numbers and fees</li>
                  <li>Each item has an effective date (commencement date) when the rate becomes active</li>
                  <li>When uploading a new schedule, historical items are preserved for billing accuracy</li>
                  <li>Items can have end dates when they are superseded by new versions</li>
                </ul>
              </div>
            </div>

            <div className="alert alert-warning">
              <div>
                <h4 className="font-bold mb-2">Billing Date Rules</h4>
                <ul className="list-disc list-inside space-y-1 opacity-80 text-sm">
                  <li>
                    <strong>Procedure done AFTER effective date:</strong> Bill using the latest schedule rate
                  </li>
                  <li>
                    <strong>Procedure done BEFORE effective date:</strong> Bill using the rate that was active on the procedure date
                  </li>
                  <li>This ensures accurate billing even when procedures are invoiced with delays</li>
                </ul>
              </div>
            </div>

            <div className="alert">
              <div>
                <h4 className="font-bold mb-2">XML File Format</h4>
                <p className="opacity-80 text-sm">
                  The system expects XML files in the standard MBS format with &lt;Data&gt; elements containing 
                  ItemNum, Description, ScheduleFee, Benefit100, and date fields. Contact support if you need 
                  assistance with the file format.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default MBSItemUpload;
